import React from "react";
import {Organization, Team, User} from "../../types/users";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextinputField from "../CommonComponents/TextInputField/TextinputField";
import StyledAutoComplete from "../CommonComponents/StyledAutocomplete/StyledAutocomplete";
import {useTheme} from "../../contexts/theme";
import TransparentButton from "../CommonComponents/TransparentButton/TransparentButton";
import {Alert, Tooltip} from "@mui/material";
import {Competition} from "../../types/competitionTypes";

interface OrganizationFormProps {
    organization: Organization;
    competitions: Competition[];
    sendFormLabel?: string,
    users: User[];
    onSubmit: () => void;
    onFieldChange: (fieldName: keyof Organization, value: string | Competition[] | User[] | Team[]) => void;
    teams: Team[]
}

const OrganizationForm: React.FC<OrganizationFormProps> = ({onFieldChange, organization, onSubmit, users, competitions, sendFormLabel = 'Send Form', teams}) => {

    const theme = useTheme()

    const [orgMessage, setOrgMessage] = React.useState<string>("")

    const stylesProps = {
        borderBottomColor: theme.palette.orange,
        marginBottom: theme.spacing.md,
        backgroundColor: 'none',
        color: theme.palette.white,
        borderColor: theme.palette.white,
        borderRadius: theme.border.width.hard,
        size: 'medium' as 'medium' ,
        width: '100%',
    }

    function handleInputChange(e:any) {
        onFieldChange("name", e.target.value)
    }

    function handleMultiSelectionChange(name: keyof Organization, selection: any[]) {
        const userArray: string[] = [];

        if (name === 'users') {
            selection.forEach(user => {
                if (user.organizations?.[0]) {
                    if (user.organizations[0].name !== organization.name) {
                        userArray.push(user.email);
                    }
                }
            });

            // Se ci sono utenti con organizzazione diversa, mostra il messaggio
            if (userArray.length > 0) {
                setOrgMessage(
                    "By selecting new users, overwrite the organization of the latter: " +
                    userArray.join(', ')
                );
            } else {
                // Altrimenti pulisci il messaggio
                setOrgMessage("");
            }
        }
        onFieldChange(name, selection);
    }


    function handleOnSubmit(e: any){
        e.preventDefault()
        onSubmit()
    }

    return (<Box
        component="form"
        onSubmit={handleOnSubmit}
    >
        {orgMessage != "" &&
            (
                <Alert variant="filled" severity="warning">
                    {orgMessage}
                </Alert>
            )

        }

        <Grid container spacing={2} sx={{marginBottom: theme.spacing.md, marginTop:theme.spacing.xs}}>
            <Grid item xs={12} md={6} lg={6}>
                <TextinputField label={"Name"} onChange={handleInputChange} value={organization.name}
                                name={'name'} placeholder={'Name'} type={'text'} variant={"outlined"}
                                required={true} {...stylesProps} />
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
                <StyledAutoComplete onChange={(_, newValue) => handleMultiSelectionChange("competitions", newValue)}
                                    value={organization.competitions} label={'Competitions'}
                                    name={'competitions'} options={competitions} labelField={'name'} valueField={'id'}
                                     />
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
                <StyledAutoComplete onChange={(_, newValue) => handleMultiSelectionChange("users", newValue)}
                                    value={organization.users} label={'Users'}
                                    name={'users'} options={users} labelField={'email'} valueField={'id'}
                                     />
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
                <StyledAutoComplete onChange={(_, newValue) => handleMultiSelectionChange("teams", newValue)}
                                    value={organization.teams} label={'Teams'} freeSolo={true} disabled={organization.competitions.length <= 0}
                                    name={'teams'} options={teams} labelField={'name'} valueField={'id'}
                />
            </Grid>
        </Grid>

        <Box width={'100%'} display={'flex'} alignItems={'center'} justifyContent={'center'}>
            <Tooltip
                title="Competition must be filled"
                // You can use `disableInteractive` if you don't want the user to be able to click on the tooltip content
                disableHoverListener={!(organization.competitions?.length === 0)}
            >
                <Box width={'200px'} height={'50px'}>
                    <TransparentButton disabled={organization.competitions?.length === 0} width={'100%'} height={'100%'}   text={sendFormLabel} type={'submit'}/>
                </Box>

            </Tooltip>

        </Box>
    </Box>)
}

export default OrganizationForm